import { Link, useLocation } from "react-router-dom";
import { Compass, Home, Package, LayoutDashboard } from "lucide-react";
import { Button, Card } from "@/components/ui-kit";

/**
 * Rota inexistente.
 *
 * Antes disso, qualquer endereço errado renderizava uma tela em branco — sem
 * aviso, sem saída. Link quebrado, URL digitada errada ou favorito antigo
 * deixavam a pessoa olhando para o nada.
 *
 * Duas variantes porque o contexto muda a saída útil:
 * - `panel`: o menu continua ao lado, então o caminho mais curto é a busca (⌘K);
 * - `store` (padrão): oferece catálogo e home, que é o que um visitante quer.
 *
 * O endereço tentado aparece na tela de propósito: quem digitou errado percebe
 * o erro, e quem seguiu um link quebrado consegue relatar qual era.
 */
export default function NotFound({ variant = "store" }) {
  const { pathname } = useLocation();
  const isPanel = variant === "panel";

  const body = (
    <div className="text-center py-12 px-6">
      <Compass size={34} className="mx-auto text-slate-300 mb-4" aria-hidden />
      <p className="font-heading text-xl font-semibold text-slate-900">
        {isPanel ? "Esta tela não existe" : "Página não encontrada"}
      </p>
      <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
        {isPanel
          ? "O endereço abaixo não corresponde a nenhuma tela do painel. Use o menu ao lado ou busque pelo nome da tela."
          : "O endereço abaixo não existe ou saiu do ar. O produto pode ter saído do catálogo."}
      </p>
      <p className="mt-3 inline-block font-mono text-xs text-slate-400 bg-slate-100 rounded-lg px-2.5 py-1 break-all max-w-full">
        {pathname}
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        {isPanel ? (
          <>
            <Link to="/admin">
              <Button icon={LayoutDashboard}>Ir para o Dashboard</Button>
            </Link>
            <span className="text-sm text-slate-400">
              ou busque com{" "}
              <kbd className="font-mono text-[11px] font-semibold border border-slate-200 rounded px-1.5 py-0.5 bg-white">⌘K</kbd>
            </span>
          </>
        ) : (
          <>
            <Link to="/produtos">
              <Button icon={Package}>Ver produtos</Button>
            </Link>
            <Link to="/">
              <Button variant="secondary" icon={Home}>Ir para a home</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );

  // No painel a tela vive dentro do layout, então precisa da superfície própria.
  // Na loja o conteúdo já respira dentro do container do StoreLayout.
  return isPanel
    ? <Card data-testid="not-found-panel">{body}</Card>
    : <div className="max-w-3xl mx-auto px-6 py-16" data-testid="not-found-store">{body}</div>;
}
